import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, PlugZap } from 'lucide-react';
import { Button } from '@/deploy/components/ui/Button';
import { Icon } from '@/deploy/components/ui/Icon';
import { getGameById } from '@/config/games';
import * as api from './profiler.api';
import { TARGET_TPS, TICK_BUDGET_MS, budgetText, headlineLevel, levelHealth, startingUp } from './profiler.health';
import { listOutcome, type GroupResult } from './profiler.servers';
import { instanceOutcome, profilerLink, type InstanceOutcome, type InstanceRef } from './profiler.instance';
import { POLL_MS, longestTickHint, spikesHourTile } from './profiler.page';
import { fmtAgo, fmtBytes, fmtClock, fmtMs, fmtTps, fmtWhen } from './profiler.format';
import { BudgetMeter, Note, StatTile, StatusLight, cx } from './ProfilerParts';
import { useProfilerGroups, type ProfilerGroup } from './useProfilerGroups';
import s from './Profiler.module.scss';
import p from './ProfilerInstancePane.module.scss';

// ============================================================
// Onglet « Profileur » de la page d'une instance (Déploiement > Environnement > une instance)
// ============================================================
// Pour ce seul serveur, ce que la page Diagnostic > Profileur en dit : le voyant et sa phrase, la date
// du dernier message reçu, et les mesures de la dernière minute. Les données sont celles que la page
// Profileur lit déjà (la liste des serveurs de chaque groupe, au satellite du projet) : rien de
// nouveau n'est collecté. Relève toutes les 30 secondes, tant que l'onglet est ouvert.
// ============================================================

interface ProfilerInstancePaneProps {
  instance: InstanceRef;
  /** État du serveur d'après la console (running, offline...) : complète la phrase d'un serveur sans mesure. */
  consoleStatus?: string | null;
}

async function loadGroup(group: ProfilerGroup): Promise<GroupResult> {
  const res = await api.listServers(group.key);
  return { key: group.key, label: group.label, ...listOutcome(res), known: null };
}

export function ProfilerInstancePane({ instance, consoleStatus }: ProfilerInstancePaneProps) {
  const navigate = useNavigate();
  const { gameId, groups, ready } = useProfilerGroups();
  const gameName = getGameById(gameId).name;
  const [results, setResults] = useState<GroupResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const run = useRef(0);

  const load = useCallback(async () => {
    const mine = ++run.current;
    setLoading(true);
    const next = await Promise.all(groups.map(loadGroup));
    if (mine !== run.current) return;
    setResults(next);
    setLoadedAt(Date.now());
    setLoading(false);
  }, [groups]);

  // Autre instance, autre projet ou autres groupes : on repart de zéro.
  useEffect(() => {
    if (!ready) return;
    setResults(null);
    void load();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void load(); }, POLL_MS);
    return () => { run.current++; window.clearInterval(timer); };
  }, [ready, load, instance.identifier]);

  const outcome = useMemo(
    () => (results ? instanceOutcome(results, instance, consoleStatus) : null),
    [results, instance, consoleStatus],
  );
  const link = profilerLink(outcome);
  const multiGroup = groups.length > 1;
  const groupOf = (key: string | null) => groups.find((g) => g.key === key) ?? null;

  return (
    <div className={p.pane}>
      <div className={cx(s.page, p.body)}>
        <div className={p.bar}>
          <span className={p.lead}>Santé de ce serveur, mesurée par le moteur</span>
          <span className={p.spacer} />
          {loadedAt && <span className={p.muted}>Mis à jour à {fmtClock(loadedAt)}</span>}
          <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />}
            loading={loading && results != null} onClick={() => void load()}>Rafraîchir</Button>
          <Button variant="secondary" size="sm" rightIcon={<Icon name="arrow-right" size={12} />}
            title={outcome?.kind === 'found' ? 'Courbes, étapes et pics de ce serveur' : 'Tous les serveurs mesurés'}
            onClick={() => navigate(link)}>
            Voir dans le profileur
          </Button>
        </div>

        {outcome == null ? <p className={s.empty}>Lecture des mesures…</p> : outcome.kind === 'unreadable' ? (
          outcome.groups.map((g) => <GroupNote key={g.key ?? ''} group={g} gameName={gameName} />)
        ) : outcome.kind === 'never' ? (
          <>
            <div className={cx(s.card, p.state)}>
              <StatusLight health={outcome.health} showReason size="lg" />
              <dl className={p.facts}>
                <div><dt>Dernier message reçu</dt><dd>jamais</dd></div>
              </dl>
            </div>
            {outcome.failed.map((g) => <GroupNote key={g.key ?? ''} group={g} gameName={gameName} />)}
          </>
        ) : (
          <Measured outcome={outcome} group={multiGroup ? groupOf(outcome.match.group.key) : null} stale={loading} />
        )}
      </div>
    </div>
  );
}

/** Serveur présent dans les mesures : voyant, dates, puis chiffres de la dernière minute s'ils arrivent. */
function Measured({ outcome, group, stale }: {
  outcome: Extract<InstanceOutcome, { kind: 'found' }>;
  group: ProfilerGroup | null;
  stale: boolean;
}) {
  const { server, health, now } = outcome.match;
  const grace = outcome.match.group.list?.startupGraceSeconds;
  const measuring = health.state === 'measuring';
  const starting = measuring && startingUp(server, now, grace);
  const level = measuring ? headlineLevel(server) : null;
  const levelLight = level && !starting ? levelHealth(level).light : 'none';
  const spikes = spikesHourTile(server, starting);
  const identity = [
    server.name,
    server.model ? `modèle ${server.model}` : null,
    server.engine ? `moteur ${server.engine}` : null,
    server.startedAt ? `démarré ${startedText(server.startedAt, now)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <>
      <div className={cx(s.card, p.state, stale && s.stale)}>
        <StatusLight health={health} showReason size="lg" />
        <dl className={p.facts}>
          <div><dt>Dernier message reçu</dt><dd>{whenText(server.lastSeenAt, now)}</dd></div>
          <div><dt>Dernières mesures</dt><dd>{server.lastSnapshotAt ? whenText(server.lastSnapshotAt, now) : 'jamais'}</dd></div>
          <div><dt>Serveur annoncé</dt><dd>{identity}</dd></div>
          {/* En texte : le badge de groupe, dans la couleur du groupe, se lit mal sur le fond sombre. */}
          {group?.label && <div><dt>Groupe</dt><dd>{group.label}</dd></div>}
        </dl>
      </div>

      <section className={cx(s.section, stale && s.stale)} aria-label="Dernière minute">
        <h3 className={p.sectionTitle}>
          Dernière minute
          {level && (
            <span className={s.sectionSub}>
              monde {level.level}{server.levels.length > 1 ? `, le plus mal en point ou le plus chargé des ${server.levels.length}` : ''}
            </span>
          )}
        </h3>
        {level ? (
          <div className={cx(s.tiles, p.tiles)}>
            <StatTile label="Ticks par seconde" value={fmtTps(level.ticksPerSecond)} hint={`${TARGET_TPS} attendus`} />
            <StatTile label="Durée d'un tick" value={level.ticks > 0 ? fmtMs(level.averageTickMs) : '-'}
              hint={level.ticks > 0 ? `en moyenne, soit ${budgetText(level.averageTickMs)} (${TICK_BUDGET_MS} ms)` : 'aucun tick fini'}>
              <BudgetMeter averageMs={level.averageTickMs} maxMs={level.ticks > 0 ? level.maxTickMs : null} light={levelLight} />
            </StatTile>
            <StatTile label="Tick le plus long" value={level.ticks > 0 ? fmtMs(level.maxTickMs) : '-'} hint={longestTickHint(level)} />
            <StatTile label="Mémoire par tick" value={fmtBytes(level.bytesPerTick)}
              hint={level.gcPauseMs > 0 ? `${fmtMs(level.gcPauseMs)} de nettoyage sur la minute` : 'aucun nettoyage sur la minute'} />
            <StatTile label="Retard" value={fmtMs(level.lagMs)} hint="à rattraper pour tenir le rythme" />
            <StatTile label="Pics (1 heure)" value={spikes.value} hint={spikes.hint} />
          </div>
        ) : (
          <p className={p.noMeasure}>Pas de mesure à afficher</p>
        )}
      </section>
    </>
  );
}

/** Satellite qui n'a pas répondu pour un groupe : la raison, avec les mots de la page Profileur. */
function GroupNote({ group, gameName }: { group: GroupResult; gameName: string }) {
  const where = group.label ? `du groupe ${group.label}` : `du projet ${gameName}`;
  if (group.error) {
    return (
      <Note tone="bad" icon={PlugZap} title={`Mesures illisibles${group.label ? ` pour le groupe ${group.label}` : ''}`}>
        {group.error} Les mesures de ce serveur ne s'affichent pas tant que ce satellite ne répond pas.
      </Note>
    );
  }
  if (group.unavailable) {
    return (
      <Note icon={Info} title={`Le profileur n'est pas encore disponible sur le satellite ${where}`}>
        Ce satellite est d'une version d'avant le profileur : il ne connaît pas encore ces mesures. Elles apparaîtront ici une fois le
        satellite mis à jour et son module « profiler » allumé. Ce n'est pas une panne.
      </Note>
    );
  }
  return (
    <Note icon={Info} title={`Le profileur n'est pas activé sur le satellite ${where}`}>
      Le module « profiler » de ce satellite est éteint : aucune mesure n'y est reçue ni gardée. Il s'allume dans son fichier de
      configuration (<code>modules.profiler</code>), puis le mod compagnon doit être posé sur les serveurs de jeu.
    </Note>
  );
}

/** « 14:02, il y a 3 min », ou « 25/10 à 14:02, il y a 22 h ». */
function whenText(t: number, now: number): string {
  return `${fmtWhen(t, now)}, ${fmtAgo(t, now)}`;
}

/** « à 06:00 » le jour même, « le 12/10 à 06:00 » sinon. */
function startedText(t: number, now: number): string {
  const when = fmtWhen(t, now);
  return when.includes(' à ') ? `le ${when}` : `à ${fmtClock(t)}`;
}
